/**
 * VivyaWatch Analytics Service
 *
 * Centralized analytics for:
 * - Visitor identification (Clearbit)
 * - Behavior tracking (Segment → Mixpanel)
 * - Session recordings (Hotjar)
 * - Lead scoring & capture
 */

import { BACKEND_URL } from '../utils/env';

// Configuration - Set these in environment variables
const CONFIG = {
  SEGMENT_WRITE_KEY: process.env.REACT_APP_SEGMENT_WRITE_KEY || '',
  HOTJAR_ID: process.env.REACT_APP_HOTJAR_ID || '',
  CLEARBIT_KEY: process.env.REACT_APP_CLEARBIT_KEY || '',
  MIXPANEL_TOKEN: process.env.REACT_APP_MIXPANEL_TOKEN || '',
  ENABLE_TRACKING: process.env.REACT_APP_ENABLE_TRACKING !== 'false',
  // Disable backend analytics calls until endpoints are implemented
  ENABLE_BACKEND_ANALYTICS: process.env.REACT_APP_ENABLE_BACKEND_ANALYTICS === 'true',
};

// Visitor session storage
const VISITOR_KEY = 'vw_visitor_id';
const SESSION_KEY = 'vw_session';
const LEAD_KEY = 'vw_lead_data';

class AnalyticsService {
  constructor() {
    this.initialized = false;
    this.visitorId = null;
    this.sessionId = null;
    this.companyData = null;
    this.pageViews = [];
    this.events = [];
    this.timeOnPage = {};
  }

  /**
   * Initialize all analytics services
   */
  async init() {
    if (this.initialized || !CONFIG.ENABLE_TRACKING) return;

    try {
      // Generate or retrieve visitor ID
      this.visitorId = this._getOrCreateVisitorId();
      this.sessionId = this._createSessionId();

      // Initialize Segment (main data pipeline)
      this._initSegment();

      // Initialize Hotjar (session recordings)
      this._initHotjar();

      // Try to identify company from IP (Clearbit)
      await this._identifyCompany();

      // Start time tracking
      this._startTimeTracking();

      // Track page visibility changes
      this._trackVisibility();

      this.initialized = true;
      console.log(' Analytics initialized', { visitorId: this.visitorId });
    } catch (error) {
      console.error('Analytics init error:', error);
    }
  }

  // =========================================================================
  // VISITOR IDENTIFICATION
  // =========================================================================

  _getOrCreateVisitorId() {
    let visitorId = localStorage.getItem(VISITOR_KEY);
    if (!visitorId) {
      visitorId = 'vw_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem(VISITOR_KEY, visitorId);
    }
    return visitorId;
  }

  _createSessionId() {
    const sessionId = 'sess_' + Date.now() + '_' + Math.random().toString(36).substr(2, 6);
    sessionStorage.setItem(SESSION_KEY, sessionId);
    return sessionId;
  }

  async _identifyCompany() {
    if (!CONFIG.CLEARBIT_KEY) return;

    try {
      // Clearbit Reveal API identifies company from IP
      const response = await fetch('https://reveal.clearbit.com/v1/companies/find?authorization=' + CONFIG.CLEARBIT_KEY, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${CONFIG.CLEARBIT_KEY}` }
      });

      if (response.ok) {
        const data = await response.json();
        this.companyData = data.company;
        
        // Store for lead scoring
        this._updateLeadData({ company: this.companyData });
        
        // Send to Segment
        if (window.analytics) {
          window.analytics.group(data.company.id, {
            name: data.company.name,
            industry: data.company.category?.industry,
            employees: data.company.metrics?.employees,
            domain: data.company.domain,
          });
        }
      }
    } catch (error) {
      // Clearbit not available or blocked - that's okay
      console.debug('Company identification skipped');
    }
  }

  // =========================================================================
  // SEGMENT INTEGRATION (Data Pipeline)
  // =========================================================================

  _initSegment() {
    if (!CONFIG.SEGMENT_WRITE_KEY || window.analytics) return;

    // Segment Analytics.js snippet
    const analytics = window.analytics = window.analytics || [];
    if (!analytics.initialize) {
      if (analytics.invoked) {
        console.error('Segment snippet included twice.');
      } else {
        analytics.invoked = true;
        analytics.methods = ['trackSubmit','trackClick','trackLink','trackForm','pageview','identify','reset','group','track','ready','alias','debug','page','once','off','on','addSourceMiddleware','addIntegrationMiddleware','setAnonymousId','addDestinationMiddleware'];
        analytics.factory = function(method) {
          return function() {
            const args = Array.prototype.slice.call(arguments);
            args.unshift(method);
            analytics.push(args);
            return analytics;
          };
        };
        for (let i = 0; i < analytics.methods.length; i++) {
          const method = analytics.methods[i];
          analytics[method] = analytics.factory(method);
        }
        analytics.load = function(key, options) {
          const script = document.createElement('script');
          script.type = 'text/javascript';
          script.async = true;
          script.src = 'https://cdn.segment.com/analytics.js/v1/' + key + '/analytics.min.js';
          const first = document.getElementsByTagName('script')[0];
          first.parentNode.insertBefore(script, first);
          analytics._loadOptions = options;
        };
        analytics._writeKey = CONFIG.SEGMENT_WRITE_KEY;
        analytics.SNIPPET_VERSION = '4.15.3';
        analytics.load(CONFIG.SEGMENT_WRITE_KEY);
      }
    }
  }

  // =========================================================================
  // HOTJAR INTEGRATION (Session Recordings & Heatmaps)
  // =========================================================================

  _initHotjar() {
    if (!CONFIG.HOTJAR_ID || window.hj) return;

    (function(h,o,t,j,a,r){
      h.hj = h.hj || function() { (h.hj.q = h.hj.q || []).push(arguments); };
      h._hjSettings = { hjid: CONFIG.HOTJAR_ID, hjsv: 6 };
      a = o.getElementsByTagName('head')[0];
      r = o.createElement('script');
      r.async = 1;
      r.src = t + h._hjSettings.hjid + j + h._hjSettings.hjsv;
      a.appendChild(r);
    })(window, document, 'https://static.hotjar.com/c/hotjar-', '.js?sv=');
  }

  // =========================================================================
  // TIME TRACKING (What visitors spend time on)
  // =========================================================================

  _startTimeTracking() {
    this.currentPage = window.location.pathname;
    this.pageStartTime = Date.now();

    // Track time when leaving page
    window.addEventListener('beforeunload', () => this._recordTimeOnPage());
  }

  _recordTimeOnPage() {
    if (!this.currentPage || !this.pageStartTime) return;

    const timeSpent = Math.round((Date.now() - this.pageStartTime) / 1000);
    this.timeOnPage[this.currentPage] = (this.timeOnPage[this.currentPage] || 0) + timeSpent;

    // Send to backend
    this._sendTimeData(this.currentPage, timeSpent);
  }

  async _sendTimeData(page, seconds) {
    // Skip if backend analytics is disabled
    if (!CONFIG.ENABLE_BACKEND_ANALYTICS) {
      return;
    }
    // Only send time tracking data for public marketing pages
    // Skip for authenticated admin dashboard pages
    if (!this._isPublicMarketingPage()) {
      return;
    }

    try {
      await fetch(`${BACKEND_URL}/api/analytics/time-on-page`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          visitorId: this.visitorId,
          sessionId: this.sessionId,
          page,
          seconds,
          timestamp: new Date().toISOString(),
        }),
      });
    } catch (e) {
      // Queue for later if offline
    }
  }

  _trackVisibility() {
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this._recordTimeOnPage();
      } else {
        this.pageStartTime = Date.now();
      }
    });
  }

  // =========================================================================
  // EVENT TRACKING (Clicks, Scrolls, Form Interactions)
  // =========================================================================

  /**
   * Track any event
   */
  track(eventName, properties = {}) {
    if (!CONFIG.ENABLE_TRACKING) return;

    const enrichedProps = {
      ...properties,
      visitorId: this.visitorId,
      sessionId: this.sessionId,
      page: window.location.pathname,
      referrer: document.referrer,
      timestamp: new Date().toISOString(),
      company: this.companyData?.name,
    };

    // Send to Segment (routes to Mixpanel, HubSpot, etc.)
    if (window.analytics) {
      window.analytics.track(eventName, enrichedProps);
    }

    // Also send to our backend for lead scoring
    this._sendToBackend('event', { eventName, ...enrichedProps });

    // Trigger Hotjar event (for filtering recordings)
    if (window.hj) {
      window.hj('event', eventName);
    }

    this.events.push({ eventName, ...enrichedProps });
  }

  /**
   * Track page view
   */
  page(pageName, properties = {}) {
    if (!CONFIG.ENABLE_TRACKING) return;

    // Record time on previous page
    this._recordTimeOnPage();

    // Update current page
    this.currentPage = window.location.pathname;
    this.pageStartTime = Date.now();

    const pageProps = {
      ...properties,
      visitorId: this.visitorId,
      sessionId: this.sessionId,
      path: window.location.pathname,
      referrer: document.referrer,
      title: document.title,
      url: window.location.href,
    };

    if (window.analytics) {
      window.analytics.page(pageName, pageProps);
    }

    this._sendToBackend('pageview', pageProps);
    this.pageViews.push(pageProps);
  }

  /**
   * Identify a user (after they provide email/info)
   */
  identify(userId, traits = {}) {
    if (!CONFIG.ENABLE_TRACKING) return;

    const enrichedTraits = {
      ...traits,
      visitorId: this.visitorId,
      company: this.companyData?.name,
      firstSeen: localStorage.getItem('vw_first_seen') || new Date().toISOString(),
      lastSeen: new Date().toISOString(),
      pageViews: this.pageViews.length,
      totalTimeOnSite: Object.values(this.timeOnPage || {}).reduce((a, b) => a + b, 0),
    };

    if (window.analytics) {
      window.analytics.identify(userId, enrichedTraits);
    }

    // Identify in Hotjar
    if (window.hj) {
      window.hj('identify', userId, enrichedTraits);
    }

    // Store as lead
    this._updateLeadData({ userId, ...enrichedTraits });
    this._sendToBackend('identify', { userId, ...enrichedTraits });
  }

  // =========================================================================
  // LEAD SCORING & CAPTURE
  // =========================================================================

  _updateLeadData(data) {
    const existing = JSON.parse(localStorage.getItem(LEAD_KEY) || '{}');
    const updated = { ...existing, ...data, updatedAt: new Date().toISOString() };
    localStorage.setItem(LEAD_KEY, JSON.stringify(updated));
  }

  getLeadScore() {
    const lead = JSON.parse(localStorage.getItem(LEAD_KEY) || '{}');
    let score = 0;

    // Page-based scoring
    const highIntentPages = ['/pricing', '/demo', '/contact', '/features'];
    const pageViewScore = this.pageViews.filter(p =>
      highIntentPages.some(hip => p.path?.includes(hip))
    ).length * 10;
    score += Math.min(pageViewScore, 50);

    // Time-based scoring
    const totalTime = Object.values(this.timeOnPage || {}).reduce((a, b) => a + b, 0);
    if (totalTime > 300) score += 20; // 5+ minutes
    if (totalTime > 600) score += 20; // 10+ minutes

    // Event-based scoring
    const highValueEvents = ['CTA Clicked', 'Demo Requested', 'Pricing Viewed', 'Video Watched'];
    const eventScore = this.events.filter(e =>
      highValueEvents.includes(e.eventName)
    ).length * 15;
    score += Math.min(eventScore, 40);

    // Company identification bonus
    if (lead.company) score += 20;
    if (lead.email) score += 30;

    return Math.min(score, 100);
  }

  /**
   * Check if current page is a public marketing page (not authenticated admin pages)
   */
  _isPublicMarketingPage() {
    const pathname = window.location.pathname;

    // List of public marketing page prefixes/paths
    const publicPaths = [
      '/',
      '/pricing',
      '/features',
      '/solutions',
      '/resources',
      '/partner',
      '/contact',
      '/privacy-policy',
      '/security-compliance',
      '/screenshot-monitoring',
      '/productivity-analytics',
      '/compliance-suite',
      '/insider-risk',
      '/tryus',
      '/compare',
      '/blog',
      '/case-studies',
      '/help',
      '/demo',
      '/signin',
      '/signup',
      '/login',
      '/sign-in',
      '/sign-up',
    ];

    // Check exact match or prefix match
    return publicPaths.some(path => {
      if (path === '/') {
        return pathname === '/';
      }
      return pathname === path || pathname.startsWith(path + '/');
    });
  }

  async _sendToBackend(type, data) {
    // Skip if backend analytics is disabled
    if (!CONFIG.ENABLE_BACKEND_ANALYTICS) {
      return;
    }
    // Only send lead tracking data for public marketing pages
    // Skip for authenticated admin dashboard pages
    if (!this._isPublicMarketingPage()) {
      return;
    }

    try {
      await fetch(`${BACKEND_URL}/api/leads/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, data, leadScore: this.getLeadScore() }),
      });
    } catch (e) {
      // Will retry later
    }
  }

  // =========================================================================
  // HIGH-INTENT EVENT HELPERS
  // =========================================================================

  trackCTAClick(ctaName, location) {
    this.track('CTA Clicked', { ctaName, location, leadScore: this.getLeadScore() });
  }

  trackPricingView(plan) {
    this.track('Pricing Viewed', { plan, leadScore: this.getLeadScore() });
  }

  trackDemoRequest(formData) {
    this.track('Demo Requested', { ...formData, leadScore: this.getLeadScore() });
  }

  trackVideoWatch(videoName, percentWatched) {
    this.track('Video Watched', { videoName, percentWatched });
  }

  trackFormStart(formName) {
    this.track('Form Started', { formName });
  }

  trackFormComplete(formName, formData) {
    this.track('Form Completed', { formName, ...formData });
    if (formData.email) {
      this.identify(formData.email, formData);
    }
  }

  trackScroll(percentage) {
    if (percentage >= 75 && !this._scrollTracked75) {
      this.track('Scrolled 75%', { page: window.location.pathname });
      this._scrollTracked75 = true;
    }
    if (percentage >= 90 && !this._scrollTracked90) {
      this.track('Scrolled 90%', { page: window.location.pathname });
      this._scrollTracked90 = true;
    }
  }

  // =========================================================================
  // EXPORT & UTILITY
  // =========================================================================

  getVisitorData() {
    return {
      visitorId: this.visitorId,
      sessionId: this.sessionId,
      company: this.companyData,
      leadScore: this.getLeadScore(),
      pageViews: this.pageViews,
      events: this.events,
      timeOnPage: this.timeOnPage,
      lead: JSON.parse(localStorage.getItem(LEAD_KEY) || '{}'),
    };
  }

  reset() {
    localStorage.removeItem(VISITOR_KEY);
    localStorage.removeItem(LEAD_KEY);
    sessionStorage.removeItem(SESSION_KEY);
    this.initialized = false;
  }
}

// Singleton instance
const analyticsService = new AnalyticsService();

export default analyticsService;
export { analyticsService };

